import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tabs,
  Tab,
  Collapse,
  TablePagination,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Select from 'react-select';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { isSuperAdmin } from '../utils/roles';
import useGetUserRole from '../hooks/useGetUserRole';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';
import useFetchDatav2 from '../hooks/useFetchDatav2';
import { scholarshipCertificateTypeOptions } from '../utils/constants/constants';

const editSchema = yup.object({
  displayName: yup.string().required('Display name is required'),
  specializationText: yup.string(),
});

const revokeSchema = yup.object({
  revocationReason: yup.string().required('Revocation reason is required'),
});

const STATUS_COLORS = {
  DRAFT: 'default',
  REVIEW: 'warning',
  ISSUED: 'success',
  CONFIRMED: 'info',
  REVOKED: 'error',
};

const btnSx = {
  backgroundColor: '#7b2d4e',
  '&:hover': { backgroundColor: '#5e2139' },
};

const ACTION_LABELS = {
  open_review: 'Open for Student Review',
  issue: 'Issue All Reviewed',
};

const CERT_TYPE_LABELS = {
  CERTIFICATE_OF_PARTICIPATION: 'Participation',
  CERTIFICATE_OF_ACHIEVEMENT: 'Achievement',
};

function CertificateBatchDashboard({ cohortId }) {
  const role = useGetUserRole();
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [revokingCertId, setRevokingCertId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [generateResult, setGenerateResult] = useState(null);
  const [flaggedExpanded, setFlaggedExpanded] = useState(false);
  const [selectedCertType, setSelectedCertType] = useState(null);
  const [coreThreshold, setCoreThreshold] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounce, setSearchDebounce] = useState('');
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);

  const queryClient = useQueryClient();
  const limit = 20;

  const { data: summaryData, isLoading: isLoadingSummary } = useFetchDatav2(
    ['certificates-summary', cohortId],
    `/cohorts/${cohortId}/certificates/summary`,
    {},
    'Could not fetch certificate summary',
    !!cohortId
  );

  const summary = summaryData?.data?.summary || summaryData?.data || {};

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounce(searchQuery);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const statusParam = statusFilter === 'all' ? '' : statusFilter;
  const typeParam = typeFilter === 'all' ? '' : typeFilter;
  const { data: certsData, isLoading: isLoadingCerts } = useFetchDatav2(
    ['certificates', cohortId, statusFilter, typeFilter, searchDebounce, page],
    `/cohorts/${cohortId}/certificates`,
    {
      params: {
        ...(statusParam ? { status: statusParam } : {}),
        ...(typeParam ? { certificate_type: typeParam } : {}),
        ...(searchDebounce ? { search: searchDebounce } : {}),
        page: page + 1,
        limit,
      },
    },
    'Could not fetch certificates',
    !!cohortId,
    true
  );

  const certificates =
    certsData?.data?.certificates || certsData?.data?.data || [];
  const totalCount =
    certsData?.data?.total || certsData?.data?.meta?.total || 0;

  const generateMutation = useMutation(
    (data) =>
      axiosInstancev2.post(`/cohorts/${cohortId}/certificates/generate`, data),
    {
      onSuccess: (response) => {
        const result = response?.data?.data || response?.data || {};
        setGenerateResult(result);
        toast.success('Certificate generation completed');
        queryClient.invalidateQueries(['certificates', cohortId]);
        queryClient.invalidateQueries(['certificates-summary', cohortId]);
      },
      onError: (error) => {
        toast.error(`Generation failed. ${error?.response?.data?.error || ''}`);
      },
    }
  );

  const editMutation = useMutation(
    ({ certId, data }) => axiosInstancev2.put(`/certificates/${certId}`, data),
    {
      onSuccess: () => {
        toast.success('Certificate updated');
        queryClient.invalidateQueries(['certificates', cohortId]);
        setEditDialogOpen(false);
        setEditingCert(null);
      },
      onError: (error) => {
        toast.error(`Update failed. ${error?.response?.data?.error || ''}`);
      },
    }
  );

  const transitionMutation = useMutation(
    (data) =>
      axiosInstancev2.post(
        `/cohorts/${cohortId}/certificates/transition`,
        data
      ),
    {
      onSuccess: () => {
        toast.success('Transition completed successfully');
        queryClient.invalidateQueries(['certificates', cohortId]);
        queryClient.invalidateQueries(['certificates-summary', cohortId]);
        setRevokeDialogOpen(false);
        setRevokingCertId(null);
        setConfirmDialogOpen(false);
        setConfirmAction(null);
      },
      onError: (error) => {
        toast.error(`Transition failed. ${error?.response?.data?.error || ''}`);
      },
    }
  );

  const editForm = useForm({
    resolver: yupResolver(editSchema),
    defaultValues: { displayName: '', specializationText: '' },
  });

  const revokeForm = useForm({
    resolver: yupResolver(revokeSchema),
    defaultValues: { revocationReason: '' },
  });

  const handleOpenEdit = (cert) => {
    setEditingCert(cert);
    editForm.reset({
      displayName: cert.display_name || '',
      specializationText: cert.specialization_text || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = (data) => {
    editMutation.mutate({
      certId: editingCert.id,
      data: {
        display_name: data.displayName,
        specialization_text: data.specializationText,
      },
    });
  };

  const handleOpenRevoke = (certId) => {
    setRevokingCertId(certId);
    revokeForm.reset({ revocationReason: '' });
    setRevokeDialogOpen(true);
  };

  const handleRevokeSubmit = (data) => {
    transitionMutation.mutate({
      action: 'revoke',
      certificate_ids: [revokingCertId],
      revocation_reason: data.revocationReason,
    });
  };

  const handleBatchAction = (action) => {
    setConfirmAction(action);
    setConfirmDialogOpen(true);
  };

  const handleConfirmBatchAction = () => {
    transitionMutation.mutate({ action: confirmAction });
  };

  const refreshCertificatesMutation = useMutation(
    () => axiosInstancev2.post(`/cohorts/${cohortId}/certificates/refresh`),
    {
      onSuccess: () => {
        toast.success('Certificates refreshed');
        queryClient.invalidateQueries(['certificates', cohortId]);
        queryClient.invalidateQueries(['certificates-summary', cohortId]);
      },
      onError: (error) => {
        toast.error(
          `Failed to refresh certificates. ${
            error?.response?.data?.error || ''
          }`
        );
      },
    }
  );

  const clearDraftsMutation = useMutation(
    () => axiosInstancev2.delete(`/cohorts/${cohortId}/certificates/drafts`),
    {
      onSuccess: (response) => {
        const msg = response?.data?.message || 'Drafts cleared';
        toast.success(msg);
        queryClient.invalidateQueries(['certificates', cohortId]);
        queryClient.invalidateQueries(['certificates-summary', cohortId]);
        setClearConfirmOpen(false);
        setGenerateResult(null);
      },
      onError: (error) => {
        toast.error(
          `Failed to clear drafts. ${error?.response?.data?.error || ''}`
        );
      },
    }
  );

  const handleGenerate = () => {
    if (!selectedCertType) {
      toast.error('Please select a certificate type');
      return;
    }
    generateMutation.mutate({
      certificate_type: selectedCertType.value,
      core_completion_threshold: coreThreshold ? parseFloat(coreThreshold) : 0,
    });
  };

  const actionLabel = ACTION_LABELS[confirmAction] || confirmAction;

  const draftCount = summary.total_draft ?? summary.draft ?? 0;
  const reviewCount = summary.total_review ?? summary.review ?? 0;
  const issuedCount = summary.total_issued ?? summary.issued ?? 0;
  const confirmedCount = summary.total_confirmed ?? summary.confirmed ?? 0;
  const pendingCrCount =
    summary.pending_change_requests ?? summary.pending_crs ?? 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-3 items-center">
        <span className="text-gray-600 font-medium">Summary:</span>
        {isLoadingSummary ? (
          <CircularProgress size={20} />
        ) : (
          <>
            <Chip label={`Draft: ${draftCount}`} variant="outlined" />
            <Chip
              label={`Review: ${reviewCount}`}
              color="warning"
              variant="outlined"
            />
            <Chip
              label={`Issued: ${issuedCount}`}
              color="success"
              variant="outlined"
            />
            <Chip
              label={`Confirmed: ${confirmedCount}`}
              color="info"
              variant="outlined"
            />
            <Chip
              label={`Pending CRs: ${pendingCrCount}`}
              color="error"
              variant="outlined"
            />
          </>
        )}
      </div>

      <Paper className="p-4">
        <div className="flex justify-between items-center mb-3">
          <h4 className="text-base font-semibold text-gray-700">
            Generate Certificates
          </h4>
          {isSuperAdmin(role) && (
            <div className="flex items-center gap-2">
              <Button
                variant="contained"
                size="small"
                onClick={() => refreshCertificatesMutation?.mutate()}
                disabled={refreshCertificatesMutation?.isLoading}
              >
                {refreshCertificatesMutation.isLoading ? (
                  <CircularProgress size={16} color="white" />
                ) : (
                  'Refresh Certificates'
                )}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => setClearConfirmOpen(true)}
                disabled={clearDraftsMutation.isLoading}
              >
                {clearDraftsMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  'Clear All Drafts'
                )}
              </Button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div className="w-72">
            <Select
              isClearable
              className="react-dropdown"
              classNamePrefix="dropdown"
              options={scholarshipCertificateTypeOptions}
              value={selectedCertType}
              onChange={(opt) => setSelectedCertType(opt)}
              placeholder="Select certificate type"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.1"
              min="0"
              max="1"
              placeholder="0.7"
              value={coreThreshold}
              onChange={(e) => setCoreThreshold(e.target.value)}
              className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
            />
            <span className="text-xs text-gray-500">
              Core threshold (tiered mode)
            </span>
          </div>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={generateMutation.isLoading}
            sx={btnSx}
          >
            {generateMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Generate Certificates'
            )}
          </Button>
        </div>
        {generateResult && (
          <div className="mt-3">
            <Alert severity="info">
              {generateResult.achievement_generated > 0 ||
              generateResult.participation_generated > 0 ? (
                <>
                  Achievement: {generateResult.achievement_generated ?? 0},
                  Participation: {generateResult.participation_generated ?? 0},
                  Skipped: {generateResult.skipped ?? 0}, Flagged:{' '}
                  {generateResult.flagged ?? 0}
                </>
              ) : (
                <>
                  Generated: {generateResult.generated ?? 0}, Flagged:{' '}
                  {generateResult.flagged ?? 0}, Skipped:{' '}
                  {generateResult.skipped ?? 0}
                </>
              )}
            </Alert>
            {(generateResult.flagged > 0 ||
              generateResult.flagged_students?.length > 0) && (
              <div className="mt-2">
                <Button
                  size="small"
                  onClick={() => setFlaggedExpanded(!flaggedExpanded)}
                  endIcon={
                    flaggedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                  }
                >
                  {flaggedExpanded ? 'Hide' : 'Show'} Flagged Students
                </Button>
                <Collapse in={flaggedExpanded}>
                  <TableContainer component={Paper} sx={{ mt: 1 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>Name</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Email</strong>
                          </TableCell>
                          <TableCell>
                            <strong>Reason</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(generateResult.flagged_students || []).map(
                          (student, idx) => (
                            <TableRow key={student.id || idx}>
                              <TableCell>
                                {student.name || student.display_name || '-'}
                              </TableCell>
                              <TableCell>{student.email || '-'}</TableCell>
                              <TableCell>
                                {student.reason || student.flag_reason || '-'}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </div>
            )}
          </div>
        )}
      </Paper>

      <Paper>
        <div className="p-3">
          <input
            type="text"
            placeholder="Search by name, email, or specialization..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
          />
        </div>
        <Tabs
          value={statusFilter}
          onChange={(e, val) => {
            setStatusFilter(val);
            setPage(0);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" value="all" />
          <Tab label="Draft" value="DRAFT" />
          <Tab label="Review" value="REVIEW" />
          <Tab label="Issued" value="ISSUED" />
        </Tabs>
        <Tabs
          value={typeFilter}
          onChange={(e, val) => {
            setTypeFilter(val);
            setPage(0);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
          textColor="secondary"
          indicatorColor="secondary"
        >
          <Tab label="All Types" value="all" />
          <Tab label="Achievement" value="CERTIFICATE_OF_ACHIEVEMENT" />
          <Tab label="Participation" value="CERTIFICATE_OF_PARTICIPATION" />
        </Tabs>

        {isLoadingCerts ? (
          <div className="flex justify-center py-8">
            <CircularProgress />
          </div>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <strong>Name</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Email</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Specialization</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Confirmed</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(certificates) && certificates.length > 0 ? (
                    certificates.map((cert) => (
                      <TableRow key={cert.id}>
                        <TableCell>
                          {cert.display_name || cert.student_name || '-'}
                        </TableCell>
                        <TableCell>
                          {cert.email || cert.student_email || '-'}
                        </TableCell>
                        <TableCell>
                          {cert.specialization_text ||
                            cert.specialization ||
                            '-'}
                        </TableCell>
                        <TableCell>
                          {CERT_TYPE_LABELS[cert.certificate_type] ||
                            cert.certificate_type ||
                            '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={cert.status}
                            color={STATUS_COLORS[cert.status] || 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {cert.student_confirmed ? (
                            <CheckCircleIcon color="success" fontSize="small" />
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell>
                          {(cert.status === 'DRAFT' ||
                            cert.status === 'REVIEW') && (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(cert)}
                              color="primary"
                              title="Edit"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {cert.status === 'ISSUED' && (
                            <IconButton
                              size="small"
                              onClick={() => handleOpenRevoke(cert.id)}
                              color="error"
                              title="Revoke"
                            >
                              <BlockIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        No certificates found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={limit}
              rowsPerPageOptions={[20]}
            />
          </>
        )}

        <div className="flex gap-3 p-4 border-t">
          <Button
            variant="outlined"
            onClick={() => handleBatchAction('open_review')}
            disabled={transitionMutation.isLoading}
          >
            Open for Student Review
          </Button>
          <Button
            variant="contained"
            onClick={() => handleBatchAction('issue')}
            disabled={transitionMutation.isLoading}
            sx={btnSx}
          >
            Issue All Reviewed
          </Button>
        </div>
      </Paper>

      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Certificate</DialogTitle>
        <DialogContent>
          <form
            id="edit-cert-form"
            onSubmit={editForm.handleSubmit(handleEditSubmit)}
            className="space-y-4 mt-2"
          >
            <TextField
              label="Display Name"
              fullWidth
              size="small"
              {...editForm.register('displayName')}
              error={!!editForm.formState.errors.displayName}
              helperText={editForm.formState.errors.displayName?.message}
            />
            <TextField
              label="Specialization Text"
              fullWidth
              size="small"
              {...editForm.register('specializationText')}
              error={!!editForm.formState.errors.specializationText}
              helperText={editForm.formState.errors.specializationText?.message}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            form="edit-cert-form"
            variant="contained"
            disabled={editMutation.isLoading}
            sx={btnSx}
          >
            {editMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revokeDialogOpen}
        onClose={() => setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke Certificate</DialogTitle>
        <DialogContent>
          <form
            id="revoke-cert-form"
            onSubmit={revokeForm.handleSubmit(handleRevokeSubmit)}
            className="space-y-4 mt-2"
          >
            <TextField
              label="Revocation Reason"
              fullWidth
              size="small"
              multiline
              rows={3}
              {...revokeForm.register('revocationReason')}
              error={!!revokeForm.formState.errors.revocationReason}
              helperText={revokeForm.formState.errors.revocationReason?.message}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
          <Button
            type="submit"
            form="revoke-cert-form"
            variant="contained"
            color="error"
            disabled={transitionMutation.isLoading}
          >
            {transitionMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Revoke'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>Are you sure you want to {actionLabel}?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmBatchAction}
            disabled={transitionMutation.isLoading}
            sx={btnSx}
          >
            {transitionMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Confirm'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={clearConfirmOpen}
        onClose={() => setClearConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Clear All Draft Certificates</DialogTitle>
        <DialogContent>
          This will permanently delete all DRAFT certificates for this cohort.
          Issued and Review certificates are not affected. This cannot be
          undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => clearDraftsMutation.mutate()}
            disabled={clearDraftsMutation.isLoading}
          >
            {clearDraftsMutation.isLoading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              'Clear All Drafts'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default CertificateBatchDashboard;
